import { useState, useEffect } from 'react';
import { listChapters, listSchools, getChapterConfigById } from '@dataconnect/generated';
import { FaBook, FaExclamationTriangle } from 'react-icons/fa';
import '../App.css';
import cloudImage from '../assets/images/cloud.png';

// import caveLessonImg from '../assets/images/cave_lesson.png';
// import cavePoemImg from '../assets/images/cave_poem.png';
// import caveCreativeWritingImg from '../assets/images/cave_creative_writing.png';
// import caveLetterImg from '../assets/images/cave_letter.png';
// import defaultCaveImg from '../assets/images/cave.png';

const ChaptersPage = ({ selectedStudent, onLogout, onSelectChapter, onSchoolDataLoad }) => { // eslint-disable-line no-unused-vars
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [schoolData, setSchoolData] = useState(null);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Get available subjects from the fetched school data
  const subjects = schoolData?.subjects?.map(subject => ({
    value: subject,
    label: subject.charAt(0) + subject.slice(1).toLowerCase()
  })) || [];

  // Fetch school data when component loads or student changes
  useEffect(() => {
    const fetchSchoolData = async () => {
      const schoolId = selectedStudent?.school?.id || selectedStudent?.schoolId;
      
      if (!schoolId) {
        setError('No school information available for this student.');
        setLoadingSchool(false);
        return;
      }

      try {
        setLoadingSchool(true);
        setError(null);
        
        // Fetch all schools and find the one matching the student's school ID
        const result = await listSchools({ limit: 5000 });
        
        if (result.data?.schools) {
          const school = result.data.schools.find(s => s.id === schoolId);
          if (school) {
            setSchoolData(school);
            if (onSchoolDataLoad) {
              onSchoolDataLoad(school);
            }
          } else {
            setError('School not found.');
          }
        } else {
          setError('Failed to load school information.');
        }
      } catch (err) {

        setError('Failed to load school information.');
      } finally {
        setLoadingSchool(false);
      }
    };

    fetchSchoolData();
  }, [selectedStudent]);

  // Fetch selected config when student changes
  useEffect(() => {
    const fetchSelectedConfig = async () => {
      if (!selectedStudent?.selectedConfigId) {
        setSelectedConfig(null);
        return;
      }

      try {
        setLoadingConfig(true);
        const result = await getChapterConfigById({ id: selectedStudent.selectedConfigId });
        
        if (result.data?.chapterConfig) {
          setSelectedConfig(result.data.chapterConfig);
        }
      } catch (err) {
        console.error('Failed to load selected config:', err);
      } finally {
        setLoadingConfig(false);
      }
    };

    fetchSelectedConfig();
  }, [selectedStudent?.selectedConfigId]);

  // Set default subject when school data is loaded
  useEffect(() => {
    if (subjects.length > 0 && !selectedSubject) {
      setSelectedSubject(subjects[0].value);
    }
  }, [subjects, selectedSubject]);

  useEffect(() => {
    const fetchChapters = async () => {
      const schoolId = selectedStudent?.school?.id || selectedStudent?.schoolId;
      
      if (!schoolId || !schoolData) {
        setLoading(false);
        return;
      }

      if (!selectedSubject) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch all chapters and filter by grade and subject
        const result = await listChapters();
        
        if (result.data?.chapters) {
          const chaptersData = result.data.chapters
            .filter(chapter => 
              (chapter.grade === selectedStudent.gradeLevel || !chapter.grade) &&
              chapter.subject === selectedSubject
            );
          
          // We no longer filter chapters by syllabus in the Learn view.
          // This allows users to learn any chapter while using the syllabus specifically for practice.
          
          setChapters(chaptersData);
        } else {
          setChapters([]);
        }
      } catch (err) {

        setError('Failed to load chapters. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [selectedStudent, selectedSubject, schoolData, selectedConfig]);

  if (loading || loadingSchool) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your chapters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle className="text-red-600 text-2xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        // sky.svg should be placed in the public/ folder
        backgroundImage: 'url(/sky.svg)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }}
    >
      {/* Main Content */}
      <div className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Subject Selection - Only show if more than one subject */}
        {subjects.length > 1 ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 mb-8">
            <h3 className="text-2xl font-bold text-center text-blue-900 mb-6">
              🎨 Choose Your Subject
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subjects.map((subject, index) => {
                const gradients = [
                  'from-indigo-500 to-purple-600',
                  'from-pink-500 to-rose-600',
                  'from-blue-500 to-cyan-600',
                  'from-green-500 to-teal-600'
                ];
                return (
                  <button
                    key={subject.value}
                    className={`p-4 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-105 shadow-lg ${
                      selectedSubject === subject.value 
                        ? `bg-gradient-to-r ${gradients[index % gradients.length]} scale-105` 
                        : 'bg-gray-400 hover:bg-gradient-to-r hover:' + gradients[index % gradients.length]
                    }`}
                    onClick={() => setSelectedSubject(subject.value)}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span className="text-2xl">{subject.value === 'KANNADA' ? '🇮🇳' : '🗣️'}</span>
                      <span>{subject.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : subjects.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8 mb-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaExclamationTriangle className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No subjects available</h3>
            <p className="text-gray-600">
              This school doesn&apos;t have any subjects enabled yet.
            </p>
          </div>
        ) : null}

        {/* Chapters List */}
        {selectedSubject && (
          <div>
            <h3 className="text-2xl font-bold text-center text-blue-900 mb-8">
              📖 {subjects.find(s => s.value === selectedSubject)?.label} Chapters
            </h3>
            
            {chapters.length > 0 ? (
              <div className="overflow-x-auto py-14">
                <div className="flex gap-10 px-2">
                  {chapters.map((chapter, index) => {
                    const rightColumnOffset = index % 2 === 1 ? 'md:translate-y-0' : 'md:-translate-y-24';

                    return (
                      <button
                        key={chapter.id}
                        type="button"
                        onClick={() => onSelectChapter(chapter)}
                        className={`flex-none w-72 sm:w-96 flex flex-col items-center cursor-pointer focus:outline-none ${rightColumnOffset}`}
                      >
                        <div
                          className="w-full aspect-[4/3] bg-center bg-no-repeat bg-contain transition-transform duration-200 hover:-translate-y-1 relative flex items-center justify-center px-6"
                          style={{ backgroundImage: `url(${cloudImage})` }}
                        >
                          <span className="max-w-[12rem] sm:max-w-[16rem] text-center text-sm sm:text-base font-extrabold text-sky-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.9)] tracking-wide uppercase break-words">
                            {chapter.title}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaBook className="text-gray-400 text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No chapters available</h3>
                <p className="text-gray-600">
                  No {subjects.find(s => s.value === selectedSubject)?.label.toLowerCase()} chapters are available for this school and grade level.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChaptersPage;
